import { useEffect, useRef, useState, useCallback } from 'react'

import { UploadSelfie as BLUploadSelfie } from './../../../utilities/BL/SelfieFan'
import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage'
import { LegalTermsModal } from './../../Commons/Modals/LegalTermsModal'


// -----------------------------------------------------------------------------
// SelfieFan.jsx
// Componente para capturar una selfie desde la cámara, previsualizar, rotar,
// y subirla con metadata { source: "selfie_fan" } y auditoría legal (LFPDPPP).
// Incluye "cooldown" para permitir otra subida hasta pasados X ms.
// -----------------------------------------------------------------------------

// Clave para persistir el cooldown entre recargas
const COOLDOWN_STORAGE_KEY = "selfiefan_next_allowed_ts";

// Versión exacta de los términos legales mostrados al aficionado
const TERMS_VERSION = "v1.0_20261017_santos";

// Helper para generar/obtener hash disociado de sesión (LFPDPPP)
const getOrCreateSessionHash = async () => {
  try {
    let token = sessionStorage.getItem("selfiefan_session_token");
    if (!token) {
      token = `${Date.now()}-${Math.random()}-${navigator.userAgent || ""}`;
      sessionStorage.setItem("selfiefan_session_token", token);
    }
    const msgBuffer = new TextEncoder().encode(token);
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return "anonymous_session_hash";
  }
};

// Utilidad: Formatea ms → "m:ss"
function msToMinSec(ms) {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * Espera a que el <video> tenga dimensiones reales (videoWidth/Height > 0).
 * Usa eventos + polling + timeout por seguridad.
 */
function waitForVideoReady(video, timeoutMs = 8000) {
  return new Promise((resolve, reject) => {
    if (!video) return reject(new Error("Referencia <video> nula"));

    if (video.videoWidth > 0 && video.videoHeight > 0) {
      return resolve();
    }

    let done = false;

    const onReady = () => {
      if (done) return;
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        done = true;
        cleanup();
        resolve();
      }
    };

    const onLoadedMetadata = () => onReady();
    const onCanPlay = () => onReady();

    const poll = () => {
      if (done) return;
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        onReady();
      } else {
        requestAnimationFrame(poll);
      }
    };

    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      cleanup();
      reject(new Error("Timeout esperando a que la cámara esté lista"));
    }, timeoutMs);

    const cleanup = () => {
      clearTimeout(timer);
      video.removeEventListener("loadedmetadata", onLoadedMetadata);
      video.removeEventListener("canplay", onCanPlay);
    };

    video.addEventListener("loadedmetadata", onLoadedMetadata);
    video.addEventListener("canplay", onCanPlay);
    poll();
  });
}

/**
 * Componente SelfieFan
 *
 * Vistas:
 * - 'intro'   : instrucciones + botón "Iniciar cámara"
 * - 'camera'  : cámara a pantalla completa + botón "Tomar foto"
 * - 'preview' : vista previa + girar/restaurar + elegir mensaje + enviar
 * - 'sent'    : confirmación + cooldown con contador
 */
const SelfieFan = ({ cooldownMs = 2 * 60 * 1000 /* default 2 minutos */ }) => {
  const { showMessage } = useFloatMessage();

  // --- Refs DOM ---
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const previewUrlRef = useRef(null);

  // --- Control de vistas ---
  const [view, setView] = useState("intro"); // 'intro' | 'camera' | 'preview' | 'sent'

  // --- Estados de cámara/subida ---
  const mediaStreamRef = useRef(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // --- Imagen/preview/rotación ---
  const [previewUrl, setPreviewUrl] = useState(null);
  const [lastBlob, setLastBlob] = useState(null); // lo que realmente se sube
  const [baseBlob, setBaseBlob] = useState(null); // blob base para giros
  const baseBitmapRef = useRef(null); // ImageBitmap o HTMLImageElement
  const [rotation, setRotation] = useState(0); // 0 | 90 | 180 | 270

  // --- Consentimiento Legal (LFPDPPP y Código de Comercio) ---
  const [termsAccepted, setTermsAccepted] = useState(false); // Desactivado de manera predeterminada
  const [showTermsModal, setShowTermsModal] = useState(false);

  // --- Mensajes de apoyo ---
  const supportMessages = { ONE: 1, TWO: 2, THREE: 3 };
  const SUPPORT_MESSAGE_BY_ID = {
    [supportMessages.ONE]: "Vamos con todo equipo",
    [supportMessages.TWO]: "Viva el San Luis",
    [supportMessages.THREE]: "Arriba las Auriazules",
  };
  const DEFAULT_SUPPORT_MESSAGE = SUPPORT_MESSAGE_BY_ID[supportMessages.ONE];
  const [supportMessageSelected, setSupportMessageSelected] = useState(0);

  // --- Límites de tamaño para controlar el archivo ---
  const MAX_WIDTH = 1280;
  const MAX_HEIGHT = 1280;

  // --- Cooldown (próxima vez permitida) ---
  const [nextAllowedTs, setNextAllowedTs] = useState(null);
  const [remainingMs, setRemainingMs] = useState(0);

  // Cargar cooldown persistido al montar
  useEffect(() => {
    const saved = localStorage.getItem(COOLDOWN_STORAGE_KEY);
    if (saved) {
      const ts = parseInt(saved, 10);
      if (!Number.isNaN(ts)) setNextAllowedTs(ts);
    }
  }, []);

  // Tick de countdown
  useEffect(() => {
    const tick = () => {
      if (!nextAllowedTs) {
        setRemainingMs(0);
        return;
      }
      const diff = nextAllowedTs - Date.now();
      setRemainingMs(Math.max(0, diff));
    };

    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [nextAllowedTs]);

  const canStartNew = !nextAllowedTs || Date.now() >= nextAllowedTs;

  // --- Helpers de imagen ---
  async function ensureBaseBitmapFromBlob(blob) {
    if (!blob) return;
    try {
      if ("createImageBitmap" in window) {
        baseBitmapRef.current = await createImageBitmap(blob);
        return;
      }
    } catch {
      // fallback continúa
    }

    const objUrl = URL.createObjectURL(blob);
    await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        baseBitmapRef.current = img;
        URL.revokeObjectURL(objUrl);
        resolve();
      };
      img.onerror = (e) => {
        URL.revokeObjectURL(objUrl);
        reject(e);
      };
      img.src = objUrl;
    });
  }

  /**
   * Renderiza desde el bitmap base → canvas aplicando la rotación,
   * actualiza el preview y el blob final (lastBlob).
   */
  async function renderFromBaseAndUpdate(rotationDeg) {
    const bmp = baseBitmapRef.current;
    if (!bmp || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const srcW = bmp.width;
    const srcH = bmp.height;

    const isQuarterTurn = rotationDeg % 180 !== 0;
    const outW = isQuarterTurn ? srcH : srcW;
    const outH = isQuarterTurn ? srcW : srcH;

    canvas.width = outW;
    canvas.height = outH;

    ctx.save();
    switch (rotationDeg % 360) {
      case 0:
        break;
      case 90:
        ctx.translate(outW, 0);
        ctx.rotate(Math.PI / 2);
        break;
      case 180:
        ctx.translate(outW, outH);
        ctx.rotate(Math.PI);
        break;
      case 270:
        ctx.translate(0, outH);
        ctx.rotate((3 * Math.PI) / 2);
        break;
      default:
        break;
    }

    ctx.drawImage(bmp, 0, 0);
    ctx.restore();

    const rotatedBlob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("No se pudo generar la imagen rotada"))),
        "image/jpeg",
        0.92
      );
    });

    const objUrl = URL.createObjectURL(rotatedBlob);
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = objUrl;
    setPreviewUrl(objUrl);
    setLastBlob(rotatedBlob);
  }

  // --- Cámara: iniciar / detener ---
  const startCamera = useCallback(async () => {
    // Si sigue en cooldown, no dejamos iniciar
    if (!canStartNew) {
      showMessage({
        type: MESSAGE_TYPE.INFO,
        text: `Podrás subir otra foto en ${msToMinSec(nextAllowedTs - Date.now())}.`,
      });
      return;
    }

    setErrorMsg(null);
    setIsStarting(true);
    setIsVideoReady(false);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "user" } },
        audio: false,
      });
      mediaStreamRef.current = stream;

      // Primero montamos la vista cámara para que el <video> exista en el DOM
      setView("camera");
    } catch (err) {
      console.error("Error al acceder a la cámara:", err);
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text:
          "¡Ups! No ha sido posible acceder a la cámara. Revisa y autoriza los permisos.",
      });
      setErrorMsg("No ha sido posible acceder a la cámara: " + err.message);
    } finally {
      setIsStarting(false);
    }
  }, [canStartNew, nextAllowedTs, showMessage]);

  const stopCamera = useCallback(() => {
    const stream = mediaStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  // Cuando la vista es "camera", conectamos el stream al <video> y esperamos a que esté listo
  useEffect(() => {
    if (view !== "camera") return;

    const v = videoRef.current;
    const stream = mediaStreamRef.current;
    if (!v || !stream) return;

    v.srcObject = stream;
    v.muted = true;
    v.setAttribute("playsinline", "true");

    let cancelled = false;

    (async () => {
      try {
        try {
          await v.play();
        } catch {
          // algunos navegadores requieren interacción del usuario; el botón se mantendrá deshabilitado hasta ready
        }
        await waitForVideoReady(v, 8000);
        if (!cancelled) setIsVideoReady(true);
      } catch (e) {
        if (cancelled) return;
        setIsVideoReady(false);
        setErrorMsg("No se pudo preparar el video: " + (e?.message ?? ""));
        showMessage({
          type: MESSAGE_TYPE.BAD,
          text: "No se pudo preparar la cámara. Intenta de nuevo.",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [view, showMessage]);

  // Cleanup global al desmontar
  useEffect(() => {
    return () => {
      stopCamera();
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, [stopCamera]);

  // --- Capturar frame del video a canvas ---
  const handleCapture = useCallback(async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    // Si por alguna razón aún no está listo, esperamos un poco más
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      try {
        await waitForVideoReady(video, 3000);
      } catch {
        showMessage({
          type: MESSAGE_TYPE.BAD,
          text: "La cámara aún no está lista. Espera un momento e inténtalo de nuevo.",
        });
        return;
      }
    }

    setIsCapturing(true);
    setErrorMsg(null);

    try {
      const ctx = canvas.getContext("2d");

      const vw = video.videoWidth;
      const vh = video.videoHeight;

      // Reducción proporcional para no generar archivos gigantes
      let targetW = vw;
      let targetH = vh;
      const ratio = vw / vh;
      if (targetW > MAX_WIDTH) {
        targetW = MAX_WIDTH;
        targetH = Math.round(MAX_WIDTH / ratio);
      }
      if (targetH > MAX_HEIGHT) {
        targetH = MAX_HEIGHT;
        targetW = Math.round(MAX_HEIGHT * ratio);
      }

      canvas.width = targetW;
      canvas.height = targetH;

      // Espejo para selfie (si no lo quieres, comenta translate/scale y el restore)
      ctx.save();
      ctx.translate(targetW, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, 0, 0, targetW, targetH);
      ctx.restore();

      // Blob base de la captura
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) =>
            b
              ? resolve(b)
              : reject(new Error("Error al generar la vista previa. Inténtalo de nuevo.")),
          "image/jpeg",
          0.92
        );
      });

      // Preparar preview inicial (rotación 0)
      setBaseBlob(blob);
      await ensureBaseBitmapFromBlob(blob);
      setRotation(0);
      await renderFromBaseAndUpdate(0);

      // Cerrar cámara y pasar a PREVIEW
      stopCamera();
      setView("preview");
    } catch (err) {
      console.error(err);
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text: err.message || "No se pudo capturar la foto correctamente.",
      });
      setErrorMsg(err.message || "No se pudo capturar la foto correctamente.");
    } finally {
      setIsCapturing(false);
    }
  }, [MAX_HEIGHT, MAX_WIDTH, showMessage, stopCamera]);

  // --- Subir imagen final ---
  const handleUpload = useCallback(async () => {
    if (!lastBlob) {
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text: "¡Ups! Debes tomar una foto antes de enviarla.",
      });
      setErrorMsg("Debes tomar una foto antes de enviarla.");
      return;
    }

    // Validación legal previa en el cliente
    if (!termsAccepted) {
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text: "Debes aceptar los Términos y la Política de privacidad antes de enviar la foto.",
      });
      setErrorMsg("Debes aceptar los Términos y la Política de privacidad antes de enviar la foto.");
      return;
    }

    setIsUploading(true);
    setErrorMsg(null);

    try {
      const supportMessage = SUPPORT_MESSAGE_BY_ID[supportMessageSelected] || DEFAULT_SUPPORT_MESSAGE;
      const sessionHash = await getOrCreateSessionHash();

      // Payload con metadata requerida y telemetría de auditoría legal (LFPDPPP)
      const payload = {
        mode: "binary",           // o "multipart" según tu DL
        blob: lastBlob,           // el archivo real
        caption: supportMessage,  // opcional
        source: "selfie_fan",     // METADATA
        termsAccepted: true,
        termsVersion: TERMS_VERSION,
        sessionHash: sessionHash,
        eventId: "santos_vs_x_20261017",
        timestampConsent: new Date().toISOString(),
      };

      const response = await BLUploadSelfie(payload);

      if (response?.success) {
        // Fijar el cooldown (persistente)
        const ts = Date.now() + cooldownMs;
        setNextAllowedTs(ts);
        localStorage.setItem(COOLDOWN_STORAGE_KEY, String(ts));

        // Ir a ENVIADO
        setView("sent");

        // Limpieza de estado crítico
        setTermsAccepted(false);
        setSupportMessageSelected(0);
      } else {
        throw new Error(response?.message || "Error desconocido al subir.");
      }
    } catch (err) {
      console.error(err);
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text:
          err.message || "Lo sentimos. Hubo un error al enviar tu foto. Inténtalo nuevamente más tarde.",
      });
      setErrorMsg(err.message || "Error al enviar la foto.");
    } finally {
      setIsUploading(false);
    }
  }, [lastBlob, termsAccepted, supportMessageSelected, cooldownMs, showMessage]);

  // --- UI ---
  return (
    <>
      <div className="container SelfieFan">
        {/* INTRO: Instrucciones + iniciar cámara */}
        {view === "intro" && (
          <div className="SelfieFan__Intro container">
            {/* <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia" /> */}
            <img src="/img/page/spins-five.png" className="LogoADSLxZtudia" />
            <p className="SelfieFan__Fan__Title">Bienvenid@ Aficionad@</p>
            <p className="SelfieFan__Fan__Text--Center">¡Apoyemos a nuestro equipo!</p>

            <ol className="SelfieFan__List">
              <li>Tómate una selfie</li>
              {/* <li>Elige el mensaje de apoyo que más te guste</li> */}
              <li>Da clic en “Enviar mi foto”</li>
            </ol>

            <p className="SelfieFan__Fan__Text--Center">
              Tu foto podría aparecer en pantalla mostrando tu apoyo al equipo
            </p>

            <div className="SelfieFan__Intro__Actions">
              <button
                className="Btn__Adsl--Red"
                style={{ backgroundColor: '#038244' }}
                onClick={startCamera}
                disabled={isStarting || !canStartNew}
                title={!canStartNew ? `Puedes tomar otra en ${msToMinSec(remainingMs)}` : ""}
              >
                {isStarting
                  ? "Abriendo cámara..."
                  : canStartNew
                  ? (
                    <>
                      <span className="bi bi-camera" /> Iniciar cámara
                    </>
                  )
                  : `Puedes tomar otra en ${msToMinSec(remainingMs)}`}
              </button>
            </div>
          </div>
        )}

        {/* CÁMARA: overlay pantalla completa + botón flotante "Tomar foto" */}
        {view === "camera" && (
          <div className="SelfieFan__Overlay">
            <div className="SelfieFan__Overlay__VideoWrapper">
              <video
                ref={videoRef}
                className="SelfieFan__Overlay__Video"
                autoPlay
                playsInline
                muted
              />
              <button
                className="SelfieFan__Overlay__CaptureBtn Btn__Adsl--Red"
                style={{ backgroundColor: '#038244' }}
                onClick={handleCapture}
                disabled={isCapturing || !isVideoReady}
              >
                {isCapturing
                  ? "Capturando..."
                  : isVideoReady
                  ? (
                    <>
                      <span className="bi bi-camera" /> Tomar foto
                    </>
                  )
                  : "Preparando cámara..."}
              </button>
            </div>
          </div>
        )}

        {/* Canvas oculto para procesar imagen */}
        <canvas ref={canvasRef} className="d-none" />

        {/* PREVIEW: imagen + girar + elegir mensaje + enviar */}
        {view === "preview" && (
          <div className="SelfieFan__Preview container">
            {/* <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia" /> */}
            <img src="/img/page/spins-five.png" className="LogoADSLxZtudia" />
            <p className="text-center">Vista previa:</p>

            {/* Acciones principales de preview */}
            <div className="SelfieFan__Preview__RotateActions">
              <button
                type="button"
                className="Btn__Adsl--RedOutline SelfieFan__Preview__RotateActions__RAButton"
                disabled={!baseBlob || isCapturing}
                onClick={async () => {
                  const newRot = (rotation + 90) % 360;
                  setRotation(newRot);
                  await renderFromBaseAndUpdate(newRot);
                }}
              >
                <i className="bi bi-arrow-clockwise" /> Girar
              </button>

              <button
                className="Btn__Adsl--RedOutline SelfieFan__Preview__RotateActions__RAButton"
                onClick={async () => {
                  // Limpiar preview y volver a cámara (no aplica cooldown hasta que se envíe)
                  setPreviewUrl(null);
                  if (previewUrlRef.current) {
                    URL.revokeObjectURL(previewUrlRef.current);
                    previewUrlRef.current = null;
                  }
                  setBaseBlob(null);
                  setLastBlob(null);
                  setRotation(0);
                  setTermsAccepted(false);
                  await startCamera();
                }}
                disabled={isStarting}
              >
                <i className="bi bi-camera" /> Tomar otra
              </button>
            </div>

            {previewUrl && (
              <img
                src={previewUrl}
                alt="Tu foto aparecerá aquí"
                className="SelfieFan__Preview__Image"
              />
            )}

            {/* Checkbox de Términos y Aviso de Privacidad Simplificado */}
            <div
              className="SelfieFan__Preview__Terms"
              style={{
                maxWidth: '520px',
                width: '100%',
                margin: '1.25rem auto 0.75rem auto',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '0 1rem',
                textAlign: 'left',
              }}
            >
              <input
                type="checkbox"
                id="termsCheckbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                style={{
                  marginTop: '3px',
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer',
                  accentColor: '#038244',
                  flexShrink: 0,
                }}
              />
              <label
                htmlFor="termsCheckbox"
                style={{
                  fontSize: '16px',
                  color: '#000',
                  cursor: 'pointer',
                  lineHeight: '1.4',
                  margin: 0,
                }}
              >
                He leído y acepto los{' '}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowTermsModal(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setShowTermsModal(true);
                    }
                  }}
                  style={{
                    color: '#4ade80',
                    textDecoration: 'underline',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                  }}
                >
                  Términos y la Política de privacidad
                </span>
                , y autorizo proyectar la foto en pantalla.
              </label>
            </div>

            {/* Enviar */}
            <div className="SelfieFan__Preview__Send">
              <button
                onClick={handleUpload}
                className="Btn__Adsl--Red"
                style={{
                  backgroundColor: termsAccepted ? '#038244' : '#4a5568',
                  cursor: termsAccepted && !isUploading ? 'pointer' : 'not-allowed',
                  opacity: termsAccepted ? 1 : 0.65,
                }}
                disabled={!lastBlob || isUploading || !termsAccepted}
              >
                {isUploading ? "Subiendo foto..." : "Enviar mi foto"}
              </button>
            </div>

            {errorMsg && (
              <p className="text-center mt-4" style={{ color: "crimson" }}>
                <strong>Error: </strong> {errorMsg}
              </p>
            )}
          </div>
        )}

        {/* ENVIADO: confirmación + botón bloqueado hasta fin del cooldown */}
        {view === "sent" && (
          <div className="SelfieFan__Sent container">
            {/* <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia" /> */}
            <img src="/img/page/spins-five.png" className="LogoADSLxZtudia" />
            <p className="fs-1 mb-0 text-success">
              <i className="bi bi-check-circle-fill" />
            </p>
            <p className="SelfieFan__Sent__Title">¡Gracias por tu apoyo!</p>
            <p className="SelfieFan__Fan__Text--Center">
              Tu foto ha sido enviada correctamente
            </p>

            <div className="SelfieFan__Sent__Actions">
              <button
                className="Btn__Adsl--RedOutline mx-auto"
                onClick={() => {
                  if (!canStartNew) return; // seguridad extra

                  // Reiniciar flujo (estado de imagen)
                  setPreviewUrl(null);
                  if (previewUrlRef.current) {
                    URL.revokeObjectURL(previewUrlRef.current);
                    previewUrlRef.current = null;
                  }
                  setBaseBlob(null);
                  setLastBlob(null);
                  setRotation(0);
                  setErrorMsg(null);
                  setSupportMessageSelected(0);
                  setTermsAccepted(false);

                  // Volver a intro
                  setView("intro");
                }}
                disabled={!canStartNew}
                title={!canStartNew ? `Puedes tomar otra foto en ${msToMinSec(remainingMs)}` : ""}
              >
                {canStartNew ? "Tomar otra foto" : `Puedes tomar otra foto en ${msToMinSec(remainingMs)}`}
              </button>
            </div>

            <hr />

            <a href='https://interaktia.com/?utm_source=selfiefan_web&utm_medium=banner&utm_campaign=fdm_clubsantos' target='_blank' className='SelfieFan__Sent__Banner'>
              <img src='/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif' alt='Certifícate con Ztudia' className='d-block m-auto SelfieFan__Sent__Banner__Image' />
            </a>
            <a href='https://interaktia.com/?utm_source=selfiefan_web&utm_medium=banner&utm_campaign=fdm_clubsantos' target='_blank' className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block'>Contactar ahora</a>

            <hr />

            <p className='text-center mb-0'>¿Quieres conocer más de esta tecnología?</p>
            <p className='text-center mb-0'>Interaktia está listo para ti</p>
            <a href='https://interaktia.com/?utm_source=selfiefan_web&utm_medium=banner_text&utm_campaign=fdm_clubsantos' target='_blank' className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center'>Contactar ahora</a>
          </div>
        )}
      </div>

      <footer className="text-center mt-3 FooterCredits">
        Hecho con ♥ por <a href='https://interaktia.com/?utm_source=selfiefan_web&utm_medium=footer&utm_campaign=fdm_clubsantos' target='_blank' className='text-dark'>Interaktia</a> | 2026
      </footer>

      {/* Modal con Términos y Aviso de Privacidad Simplificado */}
      <LegalTermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />
    </>
  );
};

export { SelfieFan };